import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import type { Logo, NavItem } from "../types";
import { Footer } from "./Footer";
import { LogoSection } from "./LogoSection";
import { NavRailItem } from "./NavRailItem";
import { NAVIGATION_RAIL_HORIZONTAL_PADDING } from "./constants";
import { useHasScrolledContent } from "./useHasScrolledContent";

type Props = {
	logo: Logo;
	items: NavItem[];
	activeItemKey?: string;
	isCollapsed?: boolean;
	privacyNoticeUrl?: string;
	mobileAppTermsUrl?: string;
	renderFab?: () => React.ReactNode;
	renderFooter?: () => React.ReactNode;
	onPressItem: (key: string) => void;
	onPressAccount: () => void;
	onPressLogo?: () => void;
	username?: string;
};

export const NavigationRail = ({
	logo,
	items,
	activeItemKey,
	isCollapsed = false,
	privacyNoticeUrl,
	mobileAppTermsUrl,
	renderFab,
	renderFooter,
	onPressItem,
	onPressAccount,
	onPressLogo,
	username,
}: Props) => {
	const theme = useAppTheme();
	const insets = useSafeAreaInsets();
	const { isShortScreen } = useDevice();

	const { hasScrolledContent, onScroll, onLayout, onContentSizeChange } =
		useHasScrolledContent();

	return (
		<Box
			height="100%"
			backgroundColor={theme.colors.surface}
			paddingTop={insets.top + (isCollapsed ? 12 : 18)}
			overflow="hidden"
		>
			<ScrollView
				style={{ flex: 1, alignSelf: "stretch", paddingBottom: 16 }}
				contentContainerStyle={{
					paddingHorizontal: isCollapsed
						? 0
						: NAVIGATION_RAIL_HORIZONTAL_PADDING,
					gap: isShortScreen ? 16 : 40,
					flexGrow: 1,
					alignItems: isCollapsed ? "center" : "stretch",
				}}
				showsVerticalScrollIndicator={false}
				onScroll={onScroll}
				onLayout={onLayout}
				onContentSizeChange={onContentSizeChange}
				scrollEventThrottle={16}
			>
				<LogoSection
					logo={logo}
					isCollapsed={isCollapsed}
					renderFab={renderFab}
					onPressLogo={onPressLogo}
				/>
				<Box flexDirection="column" gap={2}>
					{items.map((item) => (
						<NavRailItem
							key={item.key}
							collapsed={isCollapsed}
							active={activeItemKey === item.key}
							label={item.label}
							icon={item.icon}
							activeIcon={item.activeIcon}
							onPress={() => onPressItem(item.key)}
						/>
					))}
				</Box>
			</ScrollView>
			<Footer
				isCollapsed={isCollapsed}
				activeItemKey={activeItemKey}
				privacyNoticeUrl={privacyNoticeUrl}
				mobileAppTermsUrl={mobileAppTermsUrl}
				renderFooter={renderFooter}
				onPressAccount={onPressAccount}
				hasScrolledContent={hasScrolledContent}
				username={username}
			/>
		</Box>
	);
};
